// src/components/Billing.js

import { useDispatch, useSelector } from "react-redux";
import { config } from "../config";

import './scss/BillingTable.scss';
import { useEffect, useState, useCallback } from "react";
import Checkout from "./Checkout";
import { useNavigate, useParams } from "react-router-dom";
import { fetchMembers } from "../redux/Slices/memberSlice";
import AppSumoTab from "../components/AppSumo/AppSumoTab";
import SubscriptionManagement from "../components/Billing/SubscriptionManagement";
import { isWorkspaceAdmin } from "../utils/helpers";
import AccessDenied from "../components/AccessDenied";

const plans = [
    {
        name: 'Free',
        description: 'Perfect for individuals and small teams getting started with Zoobbe.',
        prices: { monthly: '$0 USD', annually: '$0 USD' },
        subTexts: { monthly: 'Free forever', annually: 'Free forever' },
        features: {
            core: [
                'Free forever',
                '15 collaborators',
                '512 MB storage per workspace'
            ],
            boards: [
                'Boards: Up to 15',
                'Unlimited cards',
                'File attachments: 20 MB per file',
                'Basic checklists',
                'Custom backgrounds & stickers'
            ],
            collaboration: [
                'Mentions & real-time notifications',
                'Last activity status for all members'
            ]
        }
    },
    {
        name: 'Standard',
        description: 'More power for growing teams: organize, customize, and collaborate better.',
        prices: { monthly: '$4.99 USD', annually: '$49.99 USD' },
        pricesId: { monthly: 'price_1RHOgxK0cSs6yAeTY4mwEMMZ', annually: 'price_1RHOgxK0cSs6yAeT38dhronu' },
        subTexts: { monthly: 'per seat / month', annually: 'Per seat / Year' },
        cta: 'Upgrade now',
        features: {
            core: [
                'Unlimited collaborators',
                'Unlimited storage',
                'Pro verified badge'
            ],
            boards: [
                'Boards: Unlimited',
                'Unlimited cards',
                'File attachments: 250 MB per file',
                'Advanced checklists',
                'Saved searches',
                'List colors',
                'Board backgrounds & stickers'
            ],
            collaboration: [
                'Last activity status for all members',
                'Online status visibility',
                'Team member management'
            ]
        }
    },
    {
        name: 'Premium',
        description: 'For teams that need insights, controls, and more collaboration power.',
        prices: { monthly: '$9.99 USD', annually: '$95.99 USD' },
        pricesId: { monthly: 'price_1RHOkkK0cSs6yAeTFtoiFIZi', annually: 'price_1RHOkkK0cSs6yAeT3u6MgEM6' },
        subTexts: { monthly: 'per seat / month', annually: 'Per seat / Year' },
        cta: 'Upgrade now',
        features: {
            core: [
                'Unlimited collaborators',
                'Unlimited storage',
                'Pro verified badge',
                'Priority support'
            ],
            boards: [
                'Boards: Unlimited',
                'Unlimited cards',
                'File attachments: 250 MB per file',
                'Advanced checklists',
                'Saved searches',
                'List colors',
                'Board backgrounds & stickers',
                'Custom stickers & emojis'
            ],
            analytics: [
                // 'Data export (JSON/CSV)',
                'Extended activity logs',
                'Performance insights'
            ],
            collaboration: [
                'Last activity status for all members',
                'Online status visibility',
                'Team member management'
            ],
            exclusive: [
                'White label branding (Premium 1 Year Exclusive)',
                'Custom domain support',
                'Remove Zoobbe branding',
                'Complete brand customization'
            ]
        }
    },
    {
        name: 'Enterprise',
        description: 'Enterprise-grade security, support, and scale. Tailored for large organizations.',
        prices: {
            monthly: 'Contact sales',
            annually: 'Billing varies with number of users'
        },
        subTexts: { monthly: '', annually: '' },
        cta: 'Contact sales',
        features: {
            core: [
                'Unlimited collaborators',
                'Unlimited storage',
                'Pro verified badge',
                'Priority support',
                'Dedicated support contact'
            ],
            boards: [
                'Boards: Unlimited',
                'Unlimited cards',
                'File attachments: 250 MB per file',
                'Advanced checklists',
                'Saved searches',
                'List colors',
                'Board backgrounds & stickers',
                'Custom stickers & emojis',
                'Public board visibility controls'
            ],
            analytics: [
                'Data export (JSON/CSV)',
                'Extended activity logs',
                'Performance insights',
                'Advanced reporting'
            ],
            collaboration: [
                'Last activity status for all members',
                'Online status visibility',
                'Team member management',
                'User role management',
                'Organization-wide permissions'
            ],
            security: [
                'Attachment restrictions',
                'SSO (Coming Soon)',
                'Custom security policies',
                'Enterprise-grade security'
            ]
        }
    }
];



function Billing() {

    const [showCheckout, setShowCheckout] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(null);

    const { shortId } = useParams();
    const dispatch = useDispatch();

    // Get current user and workspace to check admin status
    const user = useSelector((state) => state.user.user);
    const workspaces = useSelector((state) => state.workspaces.workspaces);
    const workspace = workspaces?.find(w => w.shortId === shortId);
    const userId = user?.user?._id;
    const isAdmin = isWorkspaceAdmin(workspace, userId);

    // State to store billing information including active member count
    const [billingInfo, setBillingInfo] = useState(null);

    // Use active member count from billing API instead of total count
    const userCount = billingInfo?.activeMembers || 0;

    const navigate = useNavigate();

    useEffect(() => {
        dispatch(fetchMembers({ type: 'workspace', id: shortId }));
        dispatch(fetchMembers({ type: 'guests', id: shortId }));
    }, [dispatch, shortId]);

    // Fetch billing information to get active member count
    const fetchBillingInfo = useCallback(async () => {
        try {
            const response = await fetch(`${config.API_URI}/v1/billing/workspace/${shortId}/status`, {
                credentials: 'include'
            });
            if (response.ok) {
                const data = await response.json();
                setBillingInfo(data);
            }
        } catch (error) {
            console.error('Error fetching billing info:', error);
        }
    }, [shortId]);

    useEffect(() => {
        if (shortId) {
            fetchBillingInfo();
        }
    }, [shortId, fetchBillingInfo]);




    const [billing, setBilling] = useState('annually');
    const [activeTab, setActiveTab] = useState('subscription'); // 'subscription' or 'appsumo' - Default to Subscription

    const handleCTAClick = (plan) => {

        // Only proceed to checkout if the plan has pricing IDs (excludes Free and Enterprise plans)
        if (!plan.pricesId) {
            if (plan.name === 'Enterprise') {
                // Handle Enterprise plan differently - e.g., redirect to sales contact
                alert('Please contact our sales team for Enterprise pricing.');
                return;
            }
            if (plan.name === 'Free') {
                // Handle Free plan - no checkout needed
                alert('You are already on the Free plan.');
                return;
            }
            return;
        }

        const selectedPriceId = plan.pricesId[billing];
        setShowCheckout(true);
        setSelectedPlan({
            ...plan,
            priceId: selectedPriceId
        });
    };
    const handleNavigate = () => {
        navigate('/');
    };



    // Show AccessDenied component if user is not admin
    if (!isAdmin && workspace) {
        return (
            <AccessDenied
                type="billing"
                message="Only workspace administrators can access billing and subscription management. Please contact your workspace admin if you need to make changes to the subscription."
            />
        );
    }

    return (
        <>

            {
                !showCheckout && (
                    <div className="billing-container">
                        <div className="close-button" onClick={() => handleNavigate()}>
                            <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#e3e3e3"><path d="m256-200-56-56 224-224-224-224 56-56 224 224 224-224 56 56-224 224 224 224-56 56-224-224-224 224Z" /></svg>
                        </div>


                        {/* Tab Navigation */}
                        {billingInfo && ['Free', 'AppSumo'].includes(billingInfo.planType) ? (
                            // <div className="billing-tabs">
                            //     <button
                            //         className={activeTab === 'subscription' ? 'active' : ''}
                            //         onClick={() => setActiveTab('subscription')}
                            //     >
                            //         Subscription Plans
                            //     </button>
                            //     <button
                            //         className={activeTab === 'appsumo' ? 'active' : ''}
                            //         onClick={() => setActiveTab('appsumo')}
                            //     >
                            //         AppSumo LTD
                            //     </button>
                            // </div>
                            <></>
                        ) : (
                            <div className="billing-header">
                                {billingInfo && billingInfo.planType !== 'Free' && (
                                    <SubscriptionManagement
                                        billingInfo={billingInfo}
                                        shortId={shortId}
                                        onUpdate={fetchBillingInfo}
                                    />
                                )}
                            </div>
                        )}

                        {/* Subscription Plans Tab */}
                        {(activeTab === 'subscription' || !billingInfo || !['Free', 'AppSumo'].includes(billingInfo.planType)) && (
                            <>
                                <div className="billing-toggle">
                                    <button className={billing === 'monthly' ? 'active' : ''} onClick={() => setBilling('monthly')}>Monthly</button>
                                    <button className={billing === 'annually' ? 'active' : ''} onClick={() => setBilling('annually')}>
                                        Annually <span className="discount">SAVE 17%</span>
                                    </button>
                                </div>

                                <div className="billing-cards">
                                    {plans.map((plan) => {
                                        const isCurrentPlan = billingInfo && billingInfo.planType === plan.name;
                                        const isAppSumoPlan = billingInfo && billingInfo.planType === 'AppSumo';

                                        const hasExclusive = plan.name === 'Premium' && billing === 'annually' && plan.features.exclusive;

                                        return (
                                            <div className={`card ${plan.name.toLowerCase()} ${isCurrentPlan ? 'current-plan' : ''} ${hasExclusive ? 'has-exclusive' : ''}`} key={plan.name}>
                                                <h2>
                                                    {plan.name}
                                                    {isCurrentPlan && <span className="current-badge">Current Plan</span>}
                                                </h2>
                                                <div className="description">{plan.description}</div>
                                                <div className="price">{plan.prices && plan.prices[billing]}</div>
                                                <div className="sub-text">{plan.subTexts && plan.subTexts[billing]}</div>

                                                {/* Show AppSumo info for Free plan if user has AppSumo */}
                                                {plan.name === 'Free' && isAppSumoPlan && (
                                                    <div className="appsumo-notice">
                                                        <strong>You have AppSumo {billingInfo.appSumo?.tier?.replace('tier', 'Tier ')}</strong>
                                                        <div>({billingInfo.appSumo?.seats} seats, {billingInfo.appSumo?.storage}GB storage)</div>
                                                    </div>
                                                )}

                                                {/* CTA Button - Show for all plans with CTA */}
                                                {plan.cta && (
                                                    <button className="cta-button" onClick={() => handleCTAClick(plan)}>
                                                        <span>{isCurrentPlan ? 'Purchase More Seats' : plan.cta}</span>
                                                    </button>
                                                )}

                                                <div className="plan-features">
                                                    {/* Core Features */}
                                                    <div className="feature-category">
                                                        <h5>Core Features</h5>
                                                        <ul>
                                                            {plan.features.core?.map((feature, fIdx) => (
                                                                <li key={fIdx}>
                                                                    <span className="check">✓</span>
                                                                    {feature}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>

                                                    {/* Boards Features */}
                                                    <div className="feature-category">
                                                        <h5>🗂️ Boards & Cards</h5>
                                                        <ul>
                                                            {plan.features.boards?.map((feature, fIdx) => (
                                                                <li key={fIdx}>
                                                                    <span className="check">✓</span>
                                                                    {feature}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>

                                                    {/* Analytics Features */}
                                                    {plan.features.analytics && (
                                                        <div className="feature-category">
                                                            <h5>📊 Analytics & Insights</h5>
                                                            <ul>
                                                                {plan.features.analytics.map((feature, fIdx) => (
                                                                    <li key={fIdx}>
                                                                        <span className="check">✓</span>
                                                                        {feature}
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    )}

                                                    {/* Collaboration Features */}
                                                    <div className="feature-category">
                                                        <h5>👥 Collaboration</h5>
                                                        <ul>
                                                            {plan.features.collaboration?.map((feature, fIdx) => (
                                                                <li key={fIdx}>
                                                                    <span className="check">✓</span>
                                                                    {feature}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>

                                                    {/* Security Features */}
                                                    {plan.features.security && (
                                                        <div className="feature-category">
                                                            <h5>🔒 Security & Management</h5>
                                                            <ul>
                                                                {plan.features.security.map((feature, fIdx) => (
                                                                    <li key={fIdx}>
                                                                        <span className="check">✓</span>
                                                                        {feature}
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    )}

                                                    {/* Exclusive Features for Premium Annual */}
                                                    {plan.name === 'Premium' && billing === 'annually' && plan.features.exclusive && (
                                                        <div className="feature-category exclusive">
                                                            <h5>⭐ Premium Annual Exclusive</h5>
                                                            <ul>
                                                                {plan.features.exclusive.map((feature, fIdx) => (
                                                                    <li key={fIdx}>
                                                                        <span className="check exclusive">✓</span>
                                                                        {feature}
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        )}

                        {/* AppSumo LTD Tab */}
                        {billingInfo && ['Free', 'AppSumo'].includes(billingInfo.planType) && activeTab === 'appsumo' && (
                            <AppSumoTab shortId={shortId} />
                        )}
                    </div>
                )
            }


            {
                showCheckout && (
                    <div className="checkout-page">
                        <div className="close-button" onClick={() => setShowCheckout(false)}>
                            <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#e3e3e3"><path d="m256-200-56-56 224-224-224-224 56-56 224 224 224-224 56 56-224 224 224 224-56 56-224-224-224 224Z" /></svg>
                        </div>
                        <Checkout plan={selectedPlan} billing={billing} userCount={userCount} />
                    </div>
                )
            }
        </>
    );
}

export default Billing;
